import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/authStore'

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

// Convert snake_case to camelCase
const toCamelCase = (str: string): string => {
  // Match underscore followed by letter or digit
  return str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase())
}

// Recursively transform object keys from snake_case to camelCase
const transformKeys = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(transformKeys)
  }
  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
        toCamelCase(key),
        transformKeys(value),
      ])
    )
  }
  return obj
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  transformResponse: [
    // First apply axios default JSON parsing (skip for binary data)
    (data) => {
      // Don't try to parse binary data (Blob, ArrayBuffer) or non-strings
      if (typeof data !== 'string') {
        return data
      }
      try {
        return JSON.parse(data)
      } catch {
        return data
      }
    },
    // Then transform keys to camelCase (skip for non-objects)
    (data) => {
      // Don't transform binary data or non-objects
      if (data instanceof Blob || data instanceof ArrayBuffer || typeof data !== 'object') {
        return data
      }
      return transformKeys(data)
    },
  ],
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Don't intercept auth errors from login/refresh endpoints themselves
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')

    // Handle 401 Unauthorized or 403 Forbidden (auth issues)
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry && !isAuthEndpoint) {
      // Check if this is an auth error (not a permission error)
      const errorDetail = (error.response?.data as { detail?: string })?.detail || ''
      const isAuthError = error.response?.status === 401 ||
        errorDetail.includes('credentials') ||
        errorDetail.includes('Not authenticated') ||
        errorDetail === 'Inactive user'

      if (isAuthError) {
        originalRequest._retry = true

        const refreshToken = useAuthStore.getState().refreshToken
        if (refreshToken && error.response?.status === 401) {
          try {
            const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
              refresh_token: refreshToken,
            })

            const { access_token, refresh_token } = response.data
            useAuthStore.getState().setTokens(access_token, refresh_token)

            originalRequest.headers.Authorization = `Bearer ${access_token}`
            return api(originalRequest)
          } catch (refreshError) {
            // Refresh failed, logout user
            useAuthStore.getState().logout()
            window.location.href = '/login'
            return Promise.reject(refreshError)
          }
        } else {
          // No refresh token or 403 auth error, logout user
          useAuthStore.getState().logout()
          window.location.href = '/login'
        }
      }
    }

    return Promise.reject(error)
  }
)

export default api
